import { z } from 'zod';
import { UuidSchema, IsoDateSchema, IsoDateTimeSchema, IanaTimezoneSchema } from '../common/ids.js';
import { GpsPolicySchema, DevicePolicySchema } from '../common/enums.js';
export const SiteSummarySchema = z.object({
    id: UuidSchema,
    name: z.string().min(1),
    timezone: IanaTimezoneSchema,
    /**
     * Optional (Phase 3 change batch) and nullable, exactly as the `sites`
     * columns are: a worker looking at "where am I due" wants a street, not an
     * id, and the seed leaves both `NULL` on some sites. Optional so every
     * consumer written against the pre-batch shape keeps parsing.
     */
    addressLine1: z.string().min(1).nullable().optional(),
    city: z.string().min(1).nullable().optional(),
});
const SiteWithGeoSummarySchema = z.object({
    id: UuidSchema,
    name: z.string().min(1),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    geofenceRadiusM: z.int().min(50).max(200),
    timezone: IanaTimezoneSchema,
});
export const ZoneSummarySchema = z.object({
    id: UuidSchema,
    name: z.string().min(1),
});
export const ContractSummarySchema = z.object({
    id: UuidSchema,
    clientName: z.string().min(1),
});
export const OpenSessionSchema = z.object({
    sessionId: UuidSchema,
    /** `null` for an unscheduled session (Phase 1 change batch, 2026-08-27,
     * Track C) — a worker punched in with no matching shift. */
    shiftId: UuidSchema.nullable(),
    openedAt: IsoDateTimeSchema,
    site: SiteSummarySchema,
    zone: ZoneSummarySchema.nullable(),
    /** Open past `stale_punch_threshold_h` (D-08). */
    isStale: z.boolean(),
});
export const CurrentShiftSchema = z.object({
    id: UuidSchema,
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    businessDate: IsoDateSchema,
    site: SiteWithGeoSummarySchema,
    zone: ZoneSummarySchema.nullable(),
    contract: ContractSummarySchema,
});
/** The subset of refusal codes the client needs to render inline guidance. */
export const PunchabilityReasonCodeSchema = z.enum([
    'STALE_OPEN_PUNCH',
    'OUTSIDE_PUNCH_WINDOW',
    'NO_SHIFT_SCHEDULED',
]);
export const PunchabilitySchema = z.object({
    canPunchIn: z.boolean(),
    canPunchOut: z.boolean(),
    /**
     * ADVISORY UI STATE ONLY (03-API-CONTRACT.md §2). The server re-validates
     * every rule on the punch itself; the PWA must never treat `canPunchIn:
     * true` as authorization.
     */
    reasonCode: PunchabilityReasonCodeSchema.nullable(),
    windowOpensAt: IsoDateTimeSchema,
    windowClosesAt: IsoDateTimeSchema,
});
export const PunchRequirementsSchema = z.object({
    gpsPolicy: GpsPolicySchema,
    geofenceRadiusM: z.int().min(50).max(200),
    minAccuracyM: z.int().positive(),
    devicePolicy: DevicePolicySchema,
});
/**
 * A supervisor a worker can actually reach (Phase 3 change batch).
 *
 * `phone` is `users.phone`, which is a nullable column — a supervisor with no
 * number on file reports `null` rather than an empty string, so the PWA can
 * render "no number on file" instead of a dead `tel:` link.
 */
export const SupervisorContactSchema = z.object({
    userId: UuidSchema,
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phone: z.string().min(1).nullable(),
});
/** `GET /me/context` — the single source of truth for the worker home screen. */
export const MeContextResponseSchema = z.object({
    serverTime: IsoDateTimeSchema,
    openSession: OpenSessionSchema.nullable(),
    currentShift: CurrentShiftSchema.nullable(),
    upcomingShifts: z.array(CurrentShiftSchema),
    punchability: PunchabilitySchema,
    requirements: PunchRequirementsSchema,
    /**
     * Who to call when something is wrong, at most five, in a deterministic
     * order (Phase 3 change batch).
     *
     * **Optional** so every consumer written against the pre-batch shape keeps
     * parsing: this is an additive field on a frozen response, and a required
     * one would have been a breaking change dressed as an addition.
     */
    supervisorContacts: z.array(SupervisorContactSchema).optional(),
});
//# sourceMappingURL=context.js.map